import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Button,
  TablePagination
} from '@mui/material';
import { useSelector } from 'react-redux';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RefreshIcon from '@mui/icons-material/Refresh';
import RedeemIcon from '@mui/icons-material/Redeem';
import { apiServices } from '../../../../services/apiServices';

const ReferredUsers = () => {
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [apiCount, setApiCount] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchReferredUsers = async () => {
    if (!user?.peacekeeper_id) {
      setError('Peacekeeper ID not found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiServices.getAPI(`users/referred-by/${user.peacekeeper_id}`);
      if (response && response.status >= 200 && response.status < 300) {
        const data = response.data;
        console.log('API Response:', data);

        // Handle the specific API response format
        let usersArray = [];

        if (data && data.users && Array.isArray(data.users)) {
          // API returns { message, count, peacekeeperId, users: [...] }
          usersArray = data.users;
          setApiCount(data.count || 0);
        } else if (Array.isArray(data)) {
          // Fallback: direct array response
          usersArray = data;
          setApiCount(data.length);
        } else {
          console.warn('Unexpected API response format:', data);
          usersArray = [];
          setApiCount(0);
        }

        setReferredUsers(usersArray);
        setLastUpdated(new Date());
      } else {
        throw new Error(`HTTP ${response?.status}: ${response?.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching referred users:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch referred users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferredUsers();
  }, [user?.peacekeeper_id]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Get paginated users
  const getPaginatedUsers = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return referredUsers.slice(startIndex, endIndex);
  };

  // Reset pagination when users change
  useEffect(() => {
    setPage(0);
  }, [referredUsers.length]);

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const handleRedeemRewards = (referredUser) => {
    // TODO: Implement reward redemption logic
    console.log('Redeem rewards for:', referredUser);
    alert(`Redeem rewards functionality coming soon for ${referredUser.name || referredUser.username}`);
  };

  const handleRefresh = () => {
    fetchReferredUsers();
  };

  if (loading && referredUsers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? 2 : 2
      }}>


        {/* Information Notice */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Track those who have successfully joined the <strong>I Am Peacekeeper Movement</strong> through your Certificate,
            link, or QR code.
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 1,
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-end'
        }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mr: isMobile ? 0 : 1,
              mb: isMobile ? 1 : 0,
              textAlign: isMobile ? 'center' : 'left',
              fontSize: isMobile ? '13px' : '14px'
            }}
          >
            Total Referrals: {apiCount}
          </Typography>
          {/* <Tooltip title="Refresh">
            <IconButton 
              onClick={handleRefresh} 
              disabled={loading}
              sx={{ 
                alignSelf: 'center',
                bgcolor: isMobile ? 'primary.main' : 'transparent',
                color: isMobile ? 'white' : 'inherit',
                '&:hover': { 
                  bgcolor: isMobile ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)' 
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip> */}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, fontSize: isMobile ? '13px' : '14px' }}>
          {error}
        </Alert>
      )}

      {/* Referred Users List */}
      {referredUsers.length > 0 ? (
        <Card sx={{ borderRadius: isMobile ? 2 : 1 }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <List sx={{ p: 0 }}>
              {getPaginatedUsers().map((referredUser, index) => (
                <React.Fragment key={referredUser.id || index}>
                  <ListItem
                    sx={{
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'stretch' : 'flex-start',
                      py: isMobile ? 2 : 2,
                      px: 0,
                      gap: isMobile ? 2 : 0
                    }}
                  >
                    {isMobile ? (
                      // Mobile Layout
                      <>
                        {/* User Info Section for Mobile */}
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1
                        }}>
                          <Avatar
                            sx={{
                              bgcolor: 'primary.main',
                              width: 56,
                              height: 56,
                              fontSize: '20px'
                            }}
                          >
                            {getInitials(referredUser.name || referredUser.username)}
                          </Avatar>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '18px',
                                mb: 1,
                                wordBreak: 'break-word'
                              }}
                            >
                              {referredUser.name || referredUser.username || 'Unknown User'}
                            </Typography>
                            <Chip
                              label={referredUser.payment_status || 'pending'}
                              size="small"
                              color={
                                referredUser.payment_status === 'COMPLETED' || referredUser.payment_status === 'SUCCESS'
                                  ? 'success'
                                  : referredUser.payment_status === 'PENDING'
                                    ? 'warning'
                                    : 'default'
                              }
                              sx={{
                                fontSize: '12px',
                                height: 24
                              }}
                            />
                          </Box>
                        </Box>

                        {/* User Details Section for Mobile */}
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          width: '100%'
                        }}>
                          {referredUser.email && (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              bgcolor: 'grey.50',
                              borderRadius: 1
                            }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: '14px',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {referredUser.email}
                              </Typography>
                            </Box>
                          )}

                          {referredUser.country && (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              bgcolor: 'grey.50',
                              borderRadius: 1
                            }}>
                              <LocationOnIcon fontSize="small" color="action" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '14px' }}
                              >
                                {referredUser.country}
                              </Typography>
                            </Box>
                          )}

                          {referredUser.created_at && (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              bgcolor: 'grey.50',
                              borderRadius: 1
                            }}>
                              <CalendarMonthIcon fontSize="samll" color="action" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '14px' }}
                              >
                                {formatDate(referredUser.created_at)}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Action Button for Mobile */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mt: 1
                        }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleRedeemRewards(referredUser)}
                            // sx={{ 
                            //   bgcolor: '#FF6B35',
                            //   color: 'white',
                            //   px: 2,
                            //   py: 1,
                            //   borderRadius: '25px',
                            //   fontSize: '12px',
                            //   fontWeight: 400,
                            //   '&:hover': { 
                            //     bgcolor: '#E55A2B'
                            //   }
                            // }}

                            sx={{
                              background: 'linear-gradient(to right, #214F7D, #3A8139)',
                              color: 'white',
                              borderRadius: "50px"
                            }}
                          >
                            Redeem Rewards
                          </Button>
                        </Box>
                      </>
                    ) : (
                      // Desktop Layout (unchanged)
                      <>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {getInitials(referredUser.name || referredUser.username)}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 1, flexDirection: 'row', justifyContent: 'start' }}>
                              <Typography sx={{ fontWeight: 'bold' }}>
                                {referredUser.name || referredUser.username || 'Unknown User'}
                              </Typography>
                              <Chip
                                label={referredUser.payment_status || 'pending'}
                                size="small"
                                color={
                                  referredUser.payment_status === 'COMPLETED' || referredUser.payment_status === 'SUCCESS'
                                    ? 'success'
                                    : referredUser.payment_status === 'PENDING'
                                      ? 'warning'
                                      : 'default'
                                }
                                sx={{ fontSize: '12px' }}
                              />

                              {referredUser.country && (
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LocationOnIcon fontSize="small" color="action" />
                                      <Typography variant="body2" color="text.secondary">
                                        {referredUser.country}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Grid container spacing={2}>
                                {/* {referredUser.email && (
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <EmailIcon fontSize="small" color="action" />
                                      <Typography variant="body2" color="text.secondary">
                                        {referredUser.email}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )} */}
                                
                              </Grid>

                              {referredUser.created_at && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  <strong>Referred On:</strong> {formatDate(referredUser.created_at)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />

                        {/* Action Button for Desktop */}
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          ml: 2,
                          mt: 0
                        }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleRedeemRewards(referredUser)}
                            sx={{
                              color: '#FF6B35',
                              borderColor: '#FF6B35',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 107, 53, 0.1)',
                                borderColor: '#FF6B35'
                              }
                            }}
                          >
                            Redeem
                          </Button>
                        </Box>
                      </>
                    )}
                  </ListItem>
                  {index < getPaginatedUsers().length - 1 && (
                    <Divider sx={{ mx: isMobile ? 2 : 3 }} />
                  )}
                </React.Fragment>
              ))}
            </List>

            {/* Pagination */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <TablePagination
                component="div"
                count={referredUsers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage={isMobile ? "Per page:" : "Rows per page:"}
                labelDisplayedRows={({ from, to, count }) =>
                  isMobile ? `${from}-${to} of ${count}` : `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
                sx={{
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    fontSize: isMobile ? '12px' : '14px'
                  },
                  '.MuiTablePagination-select': {
                    fontSize: isMobile ? '12px' : '14px'
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      ) : !loading ? (
        <Card sx={{ borderRadius: isMobile ? 2 : 1 }}>
          <CardContent sx={{
            textAlign: 'center',
            py: isMobile ? 6 : 4,
            px: isMobile ? 3 : 4
          }}>
            <PersonIcon sx={{
              fontSize: isMobile ? 80 : 64,
              color: 'text.secondary',
              mb: isMobile ? 3 : 2
            }} />
            <Typography
              fontWeight={600}
              color="text.secondary"
              gutterBottom
              sx={{ fontSize: isMobile ? '18px' : '20px' }}
            >
              No Referrals Yet
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: isMobile ? '14px' : '16px',
                maxWidth: isMobile ? '100%' : '400px',
                mx: 'auto'
              }}
            >
              You haven't referred any users yet. Start inviting people to join the I Am Peacekeeper Movement!
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      {/* Loading State for Refresh */}
      {loading && referredUsers.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: isMobile ? 3 : 2 }}>
          <CircularProgress size={isMobile ? 32 : 24} />
        </Box>
      )}

      {/* Last Updated Info */}
      {lastUpdated && (
        <Box sx={{
          mt: isMobile ? 3 : 2,
          textAlign: 'center',
          p: isMobile ? 2 : 0
        }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: isMobile ? '12px' : '14px',
              bgcolor: isMobile ? 'grey.100' : 'transparent',
              px: isMobile ? 2 : 0,
              py: isMobile ? 1 : 0,
              borderRadius: isMobile ? 1 : 0
            }}
          >
            Last updated: {formatDate(lastUpdated)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ReferredUsers;
